// Use relative URLs so requests go through Vite proxy in development
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const getToken = () => localStorage.getItem('auth_token');

const apiClient = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle 401 Unauthorized - clear token and redirect to login
    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
};

// Episodes - sections are stored as JSON inside the episode
export const fetchEpisodes = () => apiClient('/api/episodes');
export const fetchEpisode = (id: string) => apiClient(`/api/episodes/${id}`);
export const createEpisode = (data: { number?: number | null; title: string; date?: string | null; showTitle?: string | null }) => 
  apiClient('/api/episodes', { method: 'POST', body: JSON.stringify(data) });
export const updateEpisode = (id: string, data: { title?: string; number?: number | null; date?: string | null; showTitle?: string | null; sections?: unknown[] }) => 
  apiClient(`/api/episodes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteEpisode = (id: string) => apiClient(`/api/episodes/${id}`, { method: 'DELETE' });
export const duplicateEpisode = (id: string) => apiClient(`/api/episodes/${id}/duplicate`, { method: 'POST' });
export const exportEpisode = (id: string) => apiClient(`/api/episodes/${id}/export`);
export const importEpisode = (data: unknown) => apiClient('/api/episodes/import', { method: 'POST', body: JSON.stringify(data) });

// Settings - outro is stored as JSON inside settings
export const fetchSettings = () => apiClient('/api/settings');
export const updateSettings = (data: unknown) => apiClient('/api/settings', { method: 'PUT', body: JSON.stringify(data) });

// Uploads
export const uploadImage = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE_URL}/api/uploads`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getToken() || ''}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Upload failed');
  }

  return response.json();
};

export const deleteUpload = (filename: string) => apiClient(`/api/uploads/${filename}`, { method: 'DELETE' });
